import React from 'react';
import { optimiseImageFile } from '../utils/imageOptimisation';

const clampLogoHeight = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';
  return Math.min(Math.max(Math.round(numericValue), 24), 120);
};

function BrandUploader({
  disabled = false,
  icon,
  logo,
  logoHeight = 48,
  ogImage,
  onImageChange,
  onLogoHeightChange,
}) {
  const resolvedLogoHeight = clampLogoHeight(logoHeight) || 48;

  const handleBrandImage = (field) => async (event) => {
    if (disabled) {
      event.target.value = '';
      return;
    }

    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const optimisedFile = await optimiseImageFile(file);
      onImageChange(field, optimisedFile, URL.createObjectURL(optimisedFile));
    } catch (error) {
      console.error('Unable to optimise brand image.', error);
    }
  };

  const handleRemoveBrandImage = (field) => (event) => {
    event.preventDefault();
    onImageChange(field, null, null);
  };

  const handleLogoHeightChange = (event) => {
    onLogoHeightChange?.(clampLogoHeight(event.target.value));
  };

  const renderUploadTile = ({ alt, field, label, value }) => (
    <div className="group relative">
      <label className={`upload-tile ${disabled ? 'cursor-not-allowed opacity-60 hover:border-slate-300 hover:text-slate-500' : ''}`}>
        {value ? <img src={value} alt={alt} /> : <span>{label}</span>}
        <input disabled={disabled} type="file" accept="image/*" onChange={handleBrandImage(field)} />
      </label>
      {value && !disabled && (
        <button
          aria-label={`Remove ${label}`}
          className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full border border-slate-200 bg-white text-sm font-semibold leading-none text-slate-700 opacity-0 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 group-hover:opacity-100 focus:opacity-100"
          onClick={handleRemoveBrandImage(field)}
          title={`Remove ${label}`}
          type="button"
        >
          x
        </button>
      )}
    </div>
  );

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <p className="label-caps mb-4">Brand previews</p>
      {disabled && (
        <p className="mb-3 text-sm leading-6 text-slate-500">
          Brand images are available on paid plans.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {renderUploadTile({
          alt: 'Uploaded link icon preview',
          field: 'icon',
          label: 'Link icon',
          value: icon,
        })}
        {renderUploadTile({
          alt: 'Uploaded logo preview',
          field: 'logo',
          label: 'Logo',
          value: logo,
        })}
        {renderUploadTile({
          alt: 'Uploaded open graph preview',
          field: 'ogImage',
          label: 'OG image',
          value: ogImage,
        })}
      </div>
      <div className="mt-4">
        <label className="field-label" htmlFor="brand-logo-height">Rendered logo height</label>
        <div className="grid grid-cols-[1fr_auto] items-center gap-3">
          <input
            className="field"
            disabled={disabled}
            id="brand-logo-height"
            max="120"
            min="24"
            onChange={handleLogoHeightChange}
            type="number"
            value={resolvedLogoHeight}
          />
          <span className="text-sm font-semibold text-slate-500">px</span>
        </div>
      </div>
    </section>
  );
}

export default BrandUploader;
